import { UPLOAD_PAYLOAD_LIMIT_BINARY } from "src/constants/limits";
import reportErrorSentry from "src/lib/reportErrorSentry";

/**
 * The ceiling stops short of 1.0: size grows almost without bound near the top
 * of the range for very little visual gain, and 0.92 is what the HEIC converter
 * re-encodes at, so an iPhone photo is not degraded twice at two settings.
 */
const QUALITY_CEILING = 0.92;
const QUALITY_FLOOR = 0.3;

/** How close two quality settings have to be before the search stops splitting. */
const QUALITY_TOLERANCE = 0.02;

/**
 * Share of the bracket every step must cut off, so the search converges even for
 * content whose size curve doesn't follow the encoder's.
 */
const MIN_BRACKET_SHRINK = 0.3;

/** Good enough: a quality delta is a poor proxy for closeness to a byte target. */
const BUDGET_WELL_SPENT = 0.95;

const MIN_SCALE = 0.1;
const MAX_SCALE_STEP = 0.85;
const SCALE_ESTIMATE_MARGIN = 0.95;

/** Total encodes for one photo: enough for the search, few enough for a phone. */
const MAX_ENCODE_ATTEMPTS = 12;

const JPEG_MIME_TYPE = "image/jpeg";

const jpegNameFor = (name: string): string =>
  `${name.replace(/\.[^./\\]+$/, "")}.jpg`;

// Which subtypes actually decode differs by browser and OS, so any image/* is
// attempted and a decode failure is caught in downscaleImage below.
const isImageFile = (file: File): boolean => file.type.startsWith("image/");

const canDownscale = (): boolean =>
  typeof createImageBitmap === "function" &&
  typeof document !== "undefined" &&
  typeof HTMLCanvasElement !== "undefined";

const encodeJpeg = async (
  bitmap: ImageBitmap,
  scale: number,
  quality: number
): Promise<Blob | null> => {
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d");
  if (!context) {
    return null;
  }

  // PNGs may be transparent; without a backdrop those pixels turn black in JPEG.
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, width, height);
  context.drawImage(bitmap, 0, 0, width, height);

  return new Promise((resolve) => {
    canvas.toBlob(resolve, JPEG_MIME_TYPE, quality);
  });
};

/**
 * Quantization-table strength behind the quality knob, per the IJG/libjpeg
 * convention nearly every encoder follows. Interpolating against it instead of
 * raw quality captures most of the size curve's bend, since bit cost tracks the
 * quantization step.
 */
const ijgStrength = (quality: number): number => {
  const percent = quality * 100;
  return Math.max(percent < 50 ? 5000 / percent : 200 - 2 * percent, 0.01);
};

const ijgQuality = (strength: number): number =>
  (strength >= 100 ? 5000 / strength : (200 - strength) / 2) / 100;

/** Encodes against a shared attempt budget, remembering the smallest result. */
interface EncodeSession {
  bitmap: ImageBitmap;
  attemptsLeft: number;
  smallest: Blob | null;
}

const encode = async (
  session: EncodeSession,
  scale: number,
  quality: number
): Promise<Blob | null> => {
  if (session.attemptsLeft <= 0) {
    return null;
  }
  session.attemptsLeft--;

  const encoded = await encodeJpeg(session.bitmap, scale, quality);
  if (encoded && (!session.smallest || encoded.size < session.smallest.size)) {
    session.smallest = encoded;
  }
  return encoded;
};

type QualityOutcome =
  | { kind: "fitted"; blob: Blob }
  | { kind: "tooBig"; floorSize: number }
  | { kind: "stop" };

/**
 * Highest quality that still fits the budget at this resolution. The ceiling is
 * probed first because most photos already fit there, ending the search in a
 * single encode; if even the floor is too big its size is reported, so the
 * caller can work out how far resolution has to come down.
 */
const highestQualityWithinBudget = async (
  session: EncodeSession,
  scale: number
): Promise<QualityOutcome> => {
  const ceiling = await encode(session, scale, QUALITY_CEILING);
  if (!ceiling) {
    return { kind: "stop" };
  }
  if (ceiling.size <= UPLOAD_PAYLOAD_LIMIT_BINARY) {
    return { kind: "fitted", blob: ceiling };
  }

  const floor = await encode(session, scale, QUALITY_FLOOR);
  if (!floor) {
    return { kind: "stop" };
  }
  if (floor.size > UPLOAD_PAYLOAD_LIMIT_BINARY) {
    return { kind: "tooBig", floorSize: floor.size };
  }

  let low = { quality: QUALITY_FLOOR, size: floor.size };
  let high = { quality: QUALITY_CEILING, size: ceiling.size };
  let best = floor;

  while (high.quality - low.quality > QUALITY_TOLERANCE) {
    const lowStrength = ijgStrength(low.quality);
    const highStrength = ijgStrength(high.quality);
    const reach =
      Math.log(UPLOAD_PAYLOAD_LIMIT_BINARY / low.size) /
      Math.log(high.size / low.size);
    const guess = ijgQuality(
      lowStrength * Math.pow(highStrength / lowStrength, reach)
    );
    const span = high.quality - low.quality;
    // A degenerate bracket (equal sizes, a zero-byte encode) makes the
    // interpolation useless; plain bisection still converges.
    const quality = Number.isFinite(guess)
      ? Math.min(
          Math.max(guess, low.quality + span * MIN_BRACKET_SHRINK),
          high.quality - span * MIN_BRACKET_SHRINK
        )
      : low.quality + span / 2;

    const attempt = await encode(session, scale, quality);
    if (!attempt) {
      break;
    }

    if (attempt.size <= UPLOAD_PAYLOAD_LIMIT_BINARY) {
      best = attempt;
      low = { quality, size: attempt.size };
      if (attempt.size >= UPLOAD_PAYLOAD_LIMIT_BINARY * BUDGET_WELL_SPENT) {
        break;
      }
    } else {
      high = { quality, size: attempt.size };
    }
  }

  return { kind: "fitted", blob: best };
};

/**
 * Spends compression before resolution: the photo keeps all its pixels until
 * even {@link QUALITY_FLOOR} can't reach the budget, because JPEG artefacts
 * cost the vision model less than missing detail does. The next scale is then
 * estimated from the floor probe's measured size, since compressed size tracks
 * pixel area closely enough that one jump usually lands.
 *
 * If nothing fits, returns the smallest attempt even though it is still over
 * budget — the caller's size check rejects it either way.
 */
const encodeWithinBudget = async (
  bitmap: ImageBitmap
): Promise<Blob | null> => {
  const session: EncodeSession = {
    bitmap,
    attemptsLeft: MAX_ENCODE_ATTEMPTS,
    smallest: null
  };

  let scale = 1;
  while (scale >= MIN_SCALE) {
    const outcome = await highestQualityWithinBudget(session, scale);
    if (outcome.kind === "fitted") {
      return outcome.blob;
    }
    if (outcome.kind === "stop") {
      break;
    }

    const estimated =
      scale *
      Math.sqrt(UPLOAD_PAYLOAD_LIMIT_BINARY / outcome.floorSize) *
      SCALE_ESTIMATE_MARGIN;
    // Capped so a noisy estimate can't stall on a scale that barely moved.
    scale = Math.min(estimated, scale * MAX_SCALE_STEP);
  }

  return session.smallest;
};

/**
 * Shrinks a photo that is too large to upload: re-encodes it as JPEG, sized to
 * come in under {@link UPLOAD_PAYLOAD_LIMIT_BINARY}, so a phone camera shot
 * goes through instead of being rejected.
 *
 * Files already under the limit pass through undecoded, and the original is
 * returned whenever downscaling is impossible or would not help, so callers can
 * apply it unconditionally.
 */
export const downscaleImage = async (file: File): Promise<File> => {
  if (
    file.size <= UPLOAD_PAYLOAD_LIMIT_BINARY ||
    !isImageFile(file) ||
    !canDownscale()
  ) {
    return file;
  }

  let bitmap: ImageBitmap | undefined;
  try {
    // A canvas re-encode drops the EXIF orientation tag, so the rotation has to
    // be baked into the pixels here or portrait photos come out sideways.
    bitmap = await createImageBitmap(file, {
      imageOrientation: "from-image"
    });

    const jpeg = await encodeWithinBudget(bitmap);

    // Re-encoding can grow an already well-compressed image; keep the smaller one.
    if (!jpeg || jpeg.size >= file.size) {
      return file;
    }

    return new File([jpeg], jpegNameFor(file.name), {
      type: JPEG_MIME_TYPE,
      lastModified: file.lastModified
    });
  } catch (e) {
    reportErrorSentry(e);
    return file;
  } finally {
    bitmap?.close();
  }
};
